// Sound effects, synthesised in the browser with the Web Audio API.
//
// Nothing is downloaded: every sound is built from oscillators and filtered
// noise, in the same spirit as the procedurally generated wall textures.
// Each entry in SOUNDS is a recipe of layers; tweak the numbers to taste.

const MUTE_KEY = 'frpg.muted';

let context = null;
let master = null;
let muted = false;

try {
    muted = window.localStorage.getItem(MUTE_KEY) === '1';
} catch (err) {
    // Private browsing and blocked storage: just start unmuted
}

// Browsers only allow audio after the player interacts, so the context is
// created on first use and resumed whenever it has been suspended.
function audio() {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    if (!context) {
        context = new Ctor();
        master = context.createGain();
        master.gain.value = muted ? 0 : 0.5;
        master.connect(context.destination);
    }
    if (context.state === 'suspended') context.resume();
    return context;
}

export function primeAudio() {
    audio();
}

export function isMuted() {
    return muted;
}

export function setMuted(value) {
    muted = !!value;
    try {
        window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch (err) {
        // Nothing to do; the setting just won't be remembered
    }
    if (master) master.gain.value = muted ? 0 : 0.5;
    return muted;
}

export function toggleMuted() {
    return setMuted(!muted);
}

// A short burst of white noise, reused by every noisy layer
let noiseBuffer = null;
function noise(ctx) {
    if (!noiseBuffer || noiseBuffer.sampleRate !== ctx.sampleRate) {
        noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    }
    const source = ctx.createBufferSource();
    source.buffer = noiseBuffer;
    source.loop = true;
    return source;
}

// Fade in quickly, then die away over the layer's length
function envelope(ctx, at, { gain = 0.2, duration = 0.2, attack = 0.005 }) {
    const node = ctx.createGain();
    node.gain.setValueAtTime(0.0001, at);
    node.gain.exponentialRampToValueAtTime(gain, at + attack);
    node.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    return node;
}

// One layer: either an oscillator ({ from, to, type }) or filtered noise ({ noise: true })
function layer(ctx, at, spec) {
    const { delay = 0, duration = 0.2, filter, q = 1 } = spec;
    const start = at + delay;
    const gain = envelope(ctx, start, spec);
    let source;

    if (spec.noise) {
        source = noise(ctx);
        if (filter) {
            const biquad = ctx.createBiquadFilter();
            biquad.type = filter;
            biquad.Q.value = q;
            biquad.frequency.setValueAtTime(spec.from, start);
            if (spec.to && spec.to !== spec.from) {
                biquad.frequency.exponentialRampToValueAtTime(spec.to, start + duration);
            }
            source.connect(biquad);
            biquad.connect(gain);
        } else {
            source.connect(gain);
        }
    } else {
        source = ctx.createOscillator();
        source.type = spec.type || 'sine';
        source.frequency.setValueAtTime(spec.from, start);
        if (spec.to && spec.to !== spec.from) {
            source.frequency.exponentialRampToValueAtTime(spec.to, start + duration);
        }
        source.connect(gain);
    }

    gain.connect(master);
    source.start(start);
    source.stop(start + duration + 0.02);
}

const thud = (gain = 0.25) => ({ type: 'sine', from: 160, to: 50, duration: 0.14, gain });
const whoosh = (gain = 0.12) => ({ noise: true, filter: 'bandpass', from: 1400, to: 300, q: 1.2, duration: 0.22, gain });
const clang = (from, delay = 0, gain = 0.12) => ({ type: 'triangle', from, to: from * 0.6, duration: 0.25, gain, delay });
const note = (from, delay, duration = 0.16, type = 'triangle', gain = 0.15) => ({ type, from, duration, gain, delay });

// Rising or falling runs of notes, for the fanfares
const run = (freqs, step = 0.12, type = 'triangle', gain = 0.16) =>
    freqs.map((f, i) => note(f, i * step, step * 1.6, type, gain));

export const SOUNDS = {
    // movement and the dungeon
    step: [{ noise: true, filter: 'lowpass', from: 420, to: 140, duration: 0.11, gain: 0.13 }, { type: 'sine', from: 90, to: 55, duration: 0.09, gain: 0.12 }],
    bump: [{ noise: true, filter: 'lowpass', from: 260, to: 90, duration: 0.16, gain: 0.16 }, { type: 'sine', from: 70, to: 45, duration: 0.18, gain: 0.16 }],
    turn: [{ noise: true, filter: 'bandpass', from: 700, to: 320, q: 1.5, duration: 0.16, gain: 0.07 }],
    doorOpen: [{ noise: true, filter: 'bandpass', from: 320, to: 900, q: 9, duration: 0.45, gain: 0.1 }, { ...thud(0.16), delay: 0.42 }],
    doorClose: [{ noise: true, filter: 'bandpass', from: 900, to: 320, q: 9, duration: 0.3, gain: 0.09 }, { ...thud(0.2), delay: 0.28 }],
    chest: [{ noise: true, filter: 'lowpass', from: 900, to: 200, duration: 0.14, gain: 0.16 }, note(1180, 0.12, 0.14), note(1560, 0.2, 0.16), note(1960, 0.28, 0.2)],
    key: [note(1046, 0, 0.4, 'sine', 0.16), note(1568, 0.06, 0.5, 'sine', 0.12)],
    stairs: run([392, 523, 659, 784], 0.11),
    rest: [note(330, 0, 0.6, 'sine', 0.1), note(392, 0.18, 0.7, 'sine', 0.09), note(523, 0.36, 0.8, 'sine', 0.08)],

    bash: [{ noise: true, filter: 'lowpass', from: 500, to: 120, duration: 0.22, gain: 0.22 }, { type: 'sine', from: 110, to: 45, duration: 0.2, gain: 0.2 }],
    doorBreak: [{ noise: true, filter: 'bandpass', from: 1800, to: 200, q: 0.8, duration: 0.5, gain: 0.25 }, { type: 'sawtooth', from: 160, to: 40, duration: 0.45, gain: 0.16 }, { noise: true, filter: 'highpass', from: 2200, duration: 0.3, gain: 0.1, delay: 0.08 }],

    // combat
    combatStart: [{ type: 'sawtooth', from: 110, to: 104, duration: 0.5, gain: 0.14 }, { type: 'sawtooth', from: 165, to: 156, duration: 0.5, gain: 0.1, delay: 0.04 }, { noise: true, filter: 'lowpass', from: 300, to: 100, duration: 0.5, gain: 0.08 }],
    meleeHit: [whoosh(0.08), { ...thud(0.3), delay: 0.06 }, { ...clang(1900, 0.06, 0.1), duration: 0.12 }],
    meleeMiss: [whoosh(0.13)],
    bowShot: [{ type: 'sawtooth', from: 420, to: 130, duration: 0.14, gain: 0.14 }, { noise: true, filter: 'highpass', from: 2000, duration: 0.05, gain: 0.1 }],
    arrowHit: [{ noise: true, filter: 'highpass', from: 1800, to: 600, duration: 0.09, gain: 0.14 }, { type: 'sine', from: 260, to: 90, duration: 0.12, gain: 0.16 }],
    block: [clang(1500, 0, 0.16), clang(950, 0.03, 0.12), { noise: true, filter: 'highpass', from: 2500, duration: 0.08, gain: 0.08 }],
    flee: [{ noise: true, filter: 'bandpass', from: 1600, to: 180, q: 1, duration: 0.4, gain: 0.13 }],
    partyHurt: [{ type: 'sawtooth', from: 220, to: 70, duration: 0.26, gain: 0.16 }, { noise: true, filter: 'lowpass', from: 700, to: 150, duration: 0.2, gain: 0.12 }],
    partyDown: [{ type: 'sawtooth', from: 300, to: 60, duration: 0.7, gain: 0.18 }],
    monsterDies: [{ noise: true, filter: 'lowpass', from: 900, to: 120, duration: 0.3, gain: 0.2 }, { type: 'square', from: 150, to: 45, duration: 0.3, gain: 0.1 }],

    // spells
    spellCast: [{ type: 'sine', from: 220, to: 880, duration: 0.3, gain: 0.12 }, { noise: true, filter: 'bandpass', from: 900, to: 3000, q: 4, duration: 0.3, gain: 0.06 }],
    spellHit: [{ noise: true, filter: 'lowpass', from: 2400, to: 200, duration: 0.28, gain: 0.18 }, { type: 'square', from: 320, to: 80, duration: 0.2, gain: 0.08 }],
    heal: [note(523, 0, 0.5, 'sine', 0.13), note(659, 0.08, 0.5, 'sine', 0.12), note(784, 0.16, 0.6, 'sine', 0.11)],

    // screens
    victory: run([523, 659, 784, 1046], 0.14),
    defeat: run([392, 311, 247, 175], 0.22, 'sawtooth', 0.14),
    click: [{ type: 'square', from: 900, to: 700, duration: 0.05, gain: 0.05 }],
};

export function playSound(name, delay = 0) {
    if (muted || !SOUNDS[name]) return;
    const ctx = audio();
    if (!ctx) return;
    const at = ctx.currentTime + delay;
    try {
        SOUNDS[name].forEach((spec) => layer(ctx, at, spec));
    } catch (err) {
        console.warn('Could not play sound', name, err);
    }
}

// Play a list of cues in order, spaced out so they stay distinct
export function playSounds(names, spacing = 0.09) {
    names.forEach((name, i) => playSound(name, i * spacing));
}
